import styled from "styled-components";
import { motion } from "framer-motion";
import {
  Target,
  Users,
  PenTool,
  Code2,
  MessageSquare,
  Rocket,
  BarChart3,
} from "lucide-react";

/* ================= PAGE ================= */

const Page = styled.section`
  padding: 160px 20px;
  margin-top: -100px;
  background: radial-gradient(
      1000px at 50% 0%,
      rgba(59, 130, 246, 0.16),
      transparent 60%
    ),
    ${({ theme }) => theme.colors.bg};
`;

/* ================= CONTAINER ================= */

const Container = styled.div`
  max-width: 1100px;
  margin: auto;
`;

/* ================= HEADER ================= */

const Heading = styled(motion.h1)`
  text-align: center;
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 900;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled(motion.p)`
  max-width: 820px;
  margin: 26px auto 0;
  text-align: center;
  font-size: 18px;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= TIMELINE ================= */

const Timeline = styled.div`
  margin-top: 120px;
  display: flex;
  flex-direction: column;
  gap: 64px;
`;

/* ================= PHASE CARD ================= */

const PhaseCard = styled(motion.div)`
  position: relative;
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 32px;
  padding: 48px;
  border-radius: 28px;

  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.035),
    rgba(255, 255, 255, 0.015)
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);

  box-shadow: 0 30px 90px rgba(2, 6, 23, 0.6);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

/* ================= ICON ================= */

const IconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  color: white;
  background: ${({ color }) => color};
  box-shadow: 0 0 60px rgba(59, 130, 246, 0.45);
`;

/* ================= CONTENT ================= */

const PhaseTitle = styled.h3`
  font-size: 24px;
  font-weight: 900;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.text};
`;

const PhaseDesc = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Bullets = styled.ul`
  margin-top: 16px;
  padding-left: 18px;
  color: ${({ theme }) => theme.colors.textSecondary};

  li {
    margin-bottom: 8px;
    line-height: 1.6;
  }
`;

/* ================= ANIMATION ================= */

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

/* ================= DATA ================= */

const PHASES = [
  {
    icon: Target,
    title: "Phase 1: Requirement Understanding",
    desc: "Everything starts with clarity. No code before understanding the requirement.",
    points: [
      "Understand what problem needs to be solved",
      "Who is the user and why they need this",
      "What exact outcome is expected",
      "What is in scope vs out of scope",
      "Define clear success criteria",
      "Write requirements in simple language",
    ],
    color: "linear-gradient(135deg, #3b82f6, #60a5fa)",
  },

  {
    icon: Users,
    title: "Phase 2: Analysis & Planning",
    desc: "Analyze feasibility before building anything.",
    points: [
      "Break requirement into smaller tasks",
      "Analyze technical feasibility",
      "Identify risks and edge cases",
      "Estimate time and effort roughly",
      "Decide MVP vs future features",
      "Plan development phases realistically",
    ],
    color: "linear-gradient(135deg, #06b6d4, #22d3ee)",
  },

  {
    icon: PenTool,
    title: "Phase 3: Design (UI + System)",
    desc: "Design how the product will look and how it will work internally.",
    points: [
      "Design user flow (step by step screens)",
      "Create wireframes or rough UI",
      "Decide component structure",
      "Plan backend APIs and data models",
      "Choose tech stack wisely",
      "Think about scalability basics",
    ],
    color: "linear-gradient(135deg, #8b5cf6, #a78bfa)",
  },

  {
    icon: Code2,
    title: "Phase 4: Development",
    desc: "Convert design into working software.",
    points: [
      "Implement core features first",
      "Write clean and readable code",
      "Follow folder and naming conventions",
      "Handle basic errors and edge cases",
      "Commit code frequently",
      "Ensure app works end-to-end",
    ],
    color: "linear-gradient(135deg, #22c55e, #4ade80)",
  },

  {
    icon: MessageSquare,
    title: "Phase 5: Testing & Bug Fixing",
    desc: "Test like a user, not like a developer.",
    points: [
      "Test all main user flows manually",
      "Check edge cases and failure scenarios",
      "Fix bugs and UI issues",
      "Improve performance bottlenecks",
      "Refactor messy code if required",
      "Ensure stable and predictable behavior",
    ],
    color: "linear-gradient(135deg, #f59e0b, #fbbf24)",
  },

  {
    icon: Rocket,
    title: "Phase 6: MVP Launch",
    desc: "Launch the smallest usable version of your product.",
    points: [
      "Deploy to production environment",
      "Configure environment variables",
      "Ensure logs and monitoring exist",
      "Write basic documentation / onboarding",
      "Release to real users",
      "Observe real usage patterns",
    ],
    color: "linear-gradient(135deg, #ef4444, #f87171)",
  },

  {
    icon: BarChart3,
    title: "Phase 7: Feedback, Improve & Repeat",
    desc: "Product development never ends.",
    points: [
      "Collect user feedback actively",
      "Analyze usage data",
      "Fix real user problems first",
      "Add improvements based on demand",
      "Optimize performance if needed",
      "Repeat the same cycle again",
    ],
    color: "linear-gradient(135deg, #0ea5e9, #38bdf8)",
  },
];

/* ================= COMPONENT ================= */

export default function ProductDevelopmentPhasesPage() {
  return (
    <Page>
      <Container>
        <Heading variants={fadeUp} initial="hidden" whileInView="show">
          Product Development Phases
        </Heading>

        <Sub variants={fadeUp} initial="hidden" whileInView="show">
          A clear, repeatable system every developer should follow — whether
          you’re building a startup, SaaS, freelance product, or portfolio
          project.
        </Sub>

        <Timeline>
          {PHASES.map((phase, i) => {
            const Icon = phase.icon;
            return (
              <PhaseCard
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                <IconWrap color={phase.color}>
                  <Icon size={28} />
                </IconWrap>

                <div>
                  <PhaseTitle>{phase.title}</PhaseTitle>
                  <PhaseDesc>{phase.desc}</PhaseDesc>

                  <Bullets>
                    {phase.points.map((p, idx) => (
                      <li key={idx}>{p}</li>
                    ))}
                  </Bullets>
                </div>
              </PhaseCard>
            );
          })}
        </Timeline>
      </Container>
    </Page>
  );
}
